// src/BillingPage.js

import React from 'react';
import { supabase } from './supabaseClient';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  LinearProgress,
  Grid,
  Tabs,
  Tab,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  IconButton,
  Tooltip,
  CircularProgress,
  Alert,
  Button,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import CancelIcon from '@mui/icons-material/Cancel';
import ReplayIcon from '@mui/icons-material/Replay';

function ProgressStat({ label, used, limit }) {
  const safeLimit = typeof limit === 'number' && limit > 0 ? limit : 0;
  const safeUsed = typeof used === 'number' && used >= 0 ? used : 0;
  const pct = safeLimit > 0 ? Math.min(100, Math.round((safeUsed / safeLimit) * 100)) : 0;
  return (
    <Card variant="outlined">
      <CardContent>
        <Typography variant="subtitle2" color="text.secondary">{label}</Typography>
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mt: 1 }}>
          <Typography variant="h5">{safeUsed.toLocaleString()}</Typography>
          <Typography variant="body2" color="text.secondary">/ {safeLimit ? safeLimit.toLocaleString() : '—'}</Typography>
        </Box>
        <LinearProgress variant="determinate" value={pct} sx={{ mt: 1 }} />
        <Typography variant="caption" color="text.secondary">{pct}%</Typography>
      </CardContent>
    </Card>
  );
}

export default function BillingPage() {
  const [tab, setTab] = React.useState(0);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState('');
  const navigate = useNavigate();

  const [companyId, setCompanyId] = React.useState(null);
  const [aiSubscription, setAISubscription] = React.useState(null); // AI package subscription
  const [liveAgentSubscription, setLiveAgentSubscription] = React.useState(null); // Live Agent subscription
  const [quotas, setQuotas] = React.useState(null);
  const [invoices, setInvoices] = React.useState([]);
  const [downloading, setDownloading] = React.useState({});
  const [acting, setActing] = React.useState(false);
  
  // For backward compatibility
  const subscription = aiSubscription;

  React.useEffect(() => {
    async function load() {
      setLoading(true);
      setError('');
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) {
          setError('Not authenticated');
          setLoading(false);
          return;
        }

        const { data: prof, error: profErr } = await supabase
          .from('user_profiles')
          .select('company_id')
          .eq('user_id', user.id)
          .maybeSingle();
        if (profErr) throw profErr;
        const cid = prof?.company_id || null;
        setCompanyId(cid);

        if (cid) {
          const [{ data: aiSub }, { data: laSub }, { data: quota }, { data: inv }] = await Promise.all([
            supabase.from('company_subscriptions').select('*').eq('company_id', cid).eq('status', 'active').maybeSingle(),
            supabase.from('company_live_agent_subscriptions').select('*').eq('company_id', cid).eq('status', 'active').maybeSingle(),
            supabase.from('company_quotas').select('*').eq('company_id', cid).order('updated_at', { ascending: false }).limit(1).maybeSingle(),
            supabase.from('billing_invoices').select('*').eq('company_id', cid).order('invoice_date', { ascending: false }),
          ]);
          setAISubscription(aiSub || null);
          setLiveAgentSubscription(laSub || null);
          setQuotas(quota || null);
          setInvoices(Array.isArray(inv) ? inv : []);
        }
      } catch (e) {
        setError(e.message || String(e));
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []);

  const handleDownload = async (row) => {
    if (!row) return;
    const id = row.stripe_invoice_id;
    setDownloading(prev => ({ ...prev, [id]: true }));
    try {
      if (row.storage_path) {
        const { data, error } = await supabase.storage.from('billing-invoices').createSignedUrl(row.storage_path, 120);
        if (!error && data?.signedUrl) {
          window.open(data.signedUrl, '_blank', 'noopener,noreferrer');
          return;
        }
      }
      if (row.hosted_invoice_url) {
        window.open(row.hosted_invoice_url, '_blank', 'noopener,noreferrer');
        return;
      }
      setError('No invoice document available for download yet.');
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setDownloading(prev => ({ ...prev, [id]: false }));
    }
  };

  const messagesUsed = quotas?.used_monthly_messages || 0;
  const messagesLimit = subscription?.limit_monthly_messages || 0;
  const sessionsUsed = quotas?.used_monthly_sessions || 0;
  const sessionsLimit = subscription?.limit_monthly_sessions || 0;

  const handleCancelAtPeriodEnd = async (subscriptionType = 'ai') => {
    const subName = subscriptionType === 'ai' ? 'AI Package' : 'Live Agent';
    if (!window.confirm(`Cancel ${subName} subscription at the end of current period?`)) return;
    setActing(true);
    setError('');
    try {
      const sub = subscriptionType === 'ai' ? aiSubscription : liveAgentSubscription;
      if (!sub?.stripe_subscription_id) {
        throw new Error('No subscription ID found');
      }
      
      const { data, error } = await supabase.functions.invoke('cancel-subscription', { 
        body: { 
          subscriptionId: sub.stripe_subscription_id,
          subscriptionType 
        } 
      });
      if (error || data?.error) throw new Error(error?.message || data?.error || 'Cancel failed');
      
      // Update local state
      if (subscriptionType === 'ai') {
        setAISubscription(prev => prev ? { ...prev, cancel_at_period_end: true } : prev);
      } else {
        setLiveAgentSubscription(prev => prev ? { ...prev, cancel_at_period_end: true } : prev);
      }
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setActing(false);
    }
  };

  const handleResume = async (subscriptionType = 'ai') => {
    setActing(true);
    setError('');
    try {
      const sub = subscriptionType === 'ai' ? aiSubscription : liveAgentSubscription;
      if (!sub?.stripe_subscription_id) {
        throw new Error('No subscription ID found');
      }
      
      const { data, error } = await supabase.functions.invoke('resume-subscription', { 
        body: { 
          subscriptionId: sub.stripe_subscription_id,
          subscriptionType 
        } 
      });
      if (error || data?.error) throw new Error(error?.message || data?.error || 'Resume failed');
      
      // Update local state
      if (subscriptionType === 'ai') {
        setAISubscription(prev => prev ? { ...prev, cancel_at_period_end: false, status: 'active' } : prev);
      } else {
        setLiveAgentSubscription(prev => prev ? { ...prev, cancel_at_period_end: false, status: 'active' } : prev);
      }
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setActing(false);
    }
  };
  
  const handleManageSeats = () => {
    // Open Stripe Customer Portal to manage seat quantity
    window.open('https://billing.stripe.com/p/login/test_YOUR_PORTAL_LINK', '_blank');
  };

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', maxWidth: 980, mx: 'auto' }}>
        <Typography variant="h5">Usage & Billing</Typography>
        <Button size="small" variant="outlined" onClick={() => navigate('/chat')}>Back to Chat</Button>
      </Box>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2, maxWidth: 980, mx: 'auto' }}>
            <Tab label="Usage" />
            <Tab label="Invoices" />
          </Tabs>

          {tab === 0 && (
            <Box sx={{ maxWidth: 980, mx: 'auto' }}>
              <Grid container spacing={2} justifyContent="center">
                <Grid item xs={12} sm={6} md={5} lg={4}>
                  <ProgressStat label="Messages this month" used={messagesUsed} limit={messagesLimit} />
                </Grid>
                <Grid item xs={12} sm={6} md={5} lg={4}>
                  <ProgressStat label="Sessions this month" used={sessionsUsed} limit={sessionsLimit} />
                </Grid>
              </Grid>
              {!subscription && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  No active subscription found. <Button size="small" href="/pricing">Go to Pricing</Button>
                </Alert>
              )}
            </Box>
          )}

          {tab === 1 && (
            <Box sx={{ maxWidth: 980, mx: 'auto' }}>
              {/* AI Package Subscription */}
              <Card variant="outlined" sx={{ mb: 2 }}>
                <CardContent>
                  <Typography variant="subtitle2" color="text.secondary">AI Automation Package</Typography>
                  {aiSubscription ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 1, gap: 2, flexWrap: 'wrap' }}>
                      <Box>
                        <Typography variant="h6">{aiSubscription.package_name || 'Plan'}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          ${aiSubscription.price_usd}/month
                        </Typography>
                        
                        {/* Show coupon info if applied */}
                        {aiSubscription.stripe_promotion_code && (
                          <Box sx={{ mt: 0.5, p: 1, bgcolor: '#e8f5e9', borderRadius: 1, border: '1px solid #4caf50' }}>
                            <Typography variant="body2" sx={{ color: '#2e7d32', fontWeight: 'bold' }}>
                              🎉 Coupon Applied: {aiSubscription.stripe_promotion_code}
                            </Typography>
                            <Typography variant="caption" sx={{ color: '#2e7d32' }}>
                              {aiSubscription.coupon_percent_off 
                                ? `${aiSubscription.coupon_percent_off}% off` 
                                : aiSubscription.coupon_amount_off 
                                  ? `$${aiSubscription.coupon_amount_off} off`
                                  : 'Discount applied'}
                              {aiSubscription.coupon_duration === 'repeating' && aiSubscription.coupon_duration_months 
                                ? ` for ${aiSubscription.coupon_duration_months} months`
                                : aiSubscription.coupon_duration === 'forever' 
                                  ? ' (forever)'
                                  : ''}
                            </Typography>
                          </Box>
                        )}
                        
                        <Typography variant="body2" color="text.secondary">
                          Status: {aiSubscription.status}{aiSubscription.cancel_at_period_end ? ' (cancels at period end)' : ''}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Period: {aiSubscription.current_period_start ? new Date(aiSubscription.current_period_start).toLocaleDateString() : '—'}
                          {' - '}
                          {aiSubscription.current_period_end ? new Date(aiSubscription.current_period_end).toLocaleDateString() : '—'}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', gap: 1, flexDirection: 'column' }}>
                        <Button variant="outlined" size="small" onClick={() => navigate('/upgrade')}>
                          Change Plan
                        </Button>
                        {aiSubscription.cancel_at_period_end ? (
                          <Button variant="outlined" size="small" startIcon={<ReplayIcon />} disabled={acting} onClick={() => handleResume('ai')}>
                            Resume
                          </Button>
                        ) : (
                          <Button variant="outlined" size="small" color="error" startIcon={<CancelIcon />} disabled={acting} onClick={() => handleCancelAtPeriodEnd('ai')}>
                            Cancel
                          </Button>
                        )}
                      </Box>
                    </Box>
                  ) : (
                    <Box sx={{ mt: 1 }}>
                      <Typography variant="body2">No AI package subscription.</Typography>
                      <Button variant="contained" size="small" sx={{ mt: 1 }} onClick={() => navigate('/upgrade')}>
                        Subscribe to AI Package
                      </Button>
                    </Box>
                  )}
                </CardContent>
              </Card>
              
              {/* Live Human Agent Subscription */}
              <Card variant="outlined" sx={{ mb: 2 }}>
                <CardContent>
                  <Typography variant="subtitle2" color="text.secondary">Live Human Agent</Typography>
                  {liveAgentSubscription ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 1, gap: 2, flexWrap: 'wrap' }}>
                      <Box>
                        <Typography variant="h6">
                          {liveAgentSubscription.seat_count} {liveAgentSubscription.seat_count === 1 ? 'Seat' : 'Seats'}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          ${(liveAgentSubscription.seat_count * 19).toFixed(2)}/month ($19/seat)
                        </Typography>
                        
                        {/* Show coupon info if applied */}
                        {liveAgentSubscription.stripe_promotion_code && (
                          <Box sx={{ mt: 0.5, p: 1, bgcolor: '#e8f5e9', borderRadius: 1, border: '1px solid #4caf50' }}>
                            <Typography variant="body2" sx={{ color: '#2e7d32', fontWeight: 'bold' }}>
                              🎉 Coupon Applied: {liveAgentSubscription.stripe_promotion_code}
                            </Typography>
                            <Typography variant="caption" sx={{ color: '#2e7d32' }}>
                              {liveAgentSubscription.coupon_percent_off 
                                ? `${liveAgentSubscription.coupon_percent_off}% off` 
                                : liveAgentSubscription.coupon_amount_off 
                                  ? `$${liveAgentSubscription.coupon_amount_off} off`
                                  : 'Discount applied'}
                              {liveAgentSubscription.coupon_duration === 'repeating' && liveAgentSubscription.coupon_duration_months 
                                ? ` for ${liveAgentSubscription.coupon_duration_months} months`
                                : liveAgentSubscription.coupon_duration === 'forever' 
                                  ? ' (forever)'
                                  : ''}
                            </Typography>
                          </Box>
                        )}
                        
                        <Typography variant="body2" color="text.secondary">
                          Status: {liveAgentSubscription.status}{liveAgentSubscription.cancel_at_period_end ? ' (cancels at period end)' : ''}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Period: {liveAgentSubscription.current_period_start ? new Date(liveAgentSubscription.current_period_start).toLocaleDateString() : '—'}
                          {' - '}
                          {liveAgentSubscription.current_period_end ? new Date(liveAgentSubscription.current_period_end).toLocaleDateString() : '—'}
                        </Typography>
                        {quotas && quotas.live_agent_seats_used !== undefined && (
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                            Assigned: {quotas.live_agent_seats_used} / {liveAgentSubscription.seat_count}
                          </Typography>
                        )}
                      </Box>
                      <Box sx={{ display: 'flex', gap: 1, flexDirection: 'column' }}>
                        <Button variant="outlined" size="small" onClick={handleManageSeats}>
                          Manage Seats
                        </Button>
                        {liveAgentSubscription.cancel_at_period_end ? (
                          <Button variant="outlined" size="small" startIcon={<ReplayIcon />} disabled={acting} onClick={() => handleResume('live_agent')}>
                            Resume
                          </Button>
                        ) : (
                          <Button variant="outlined" size="small" color="error" startIcon={<CancelIcon />} disabled={acting} onClick={() => handleCancelAtPeriodEnd('live_agent')}>
                            Cancel
                          </Button>
                        )}
                      </Box>
                    </Box>
                  ) : (
                    <Box sx={{ mt: 1 }}>
                      <Typography variant="body2">No Live Agent subscription.</Typography>
                      <Button variant="contained" size="small" sx={{ mt: 1 }} onClick={() => navigate('/upgrade')}>
                        Subscribe to Live Agent
                      </Button>
                    </Box>
                  )}
                </CardContent>
              </Card>
              
              {/* Total Monthly Cost */}
              {(aiSubscription || liveAgentSubscription) && (
                <Card variant="outlined" sx={{ mb: 2, bgcolor: 'action.hover' }}>
                  <CardContent>
                    <Typography variant="subtitle2" color="text.secondary">Total Monthly Cost</Typography>
                    <Typography variant="h4" sx={{ mt: 1 }}>
                      ${(
                        (aiSubscription?.price_usd || 0) + 
                        ((liveAgentSubscription?.seat_count || 0) * 19)
                      ).toFixed(2)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {aiSubscription && `AI Package: $${aiSubscription.price_usd}`}
                      {aiSubscription && liveAgentSubscription && ' + '}
                      {liveAgentSubscription && `Live Agent: $${(liveAgentSubscription.seat_count * 19).toFixed(2)}`}
                    </Typography>
                  </CardContent>
                </Card>
              )}

              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>Invoice #</TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>Date</TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>Period</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell align="right">Total</TableCell>
                      <TableCell align="right">Action</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {Array.isArray(invoices) && invoices.length > 0 ? (
                      invoices.map((row) => (
                        <TableRow key={row.id} hover>
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>{row.invoice_number || row.stripe_invoice_id}</TableCell>
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>{row.invoice_date ? new Date(row.invoice_date).toLocaleDateString() : '—'}</TableCell>
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>{row.period_start ? new Date(row.period_start).toLocaleDateString() : '—'}
                            {' - '}
                            {row.period_end ? new Date(row.period_end).toLocaleDateString() : '—'}</TableCell>
                          <TableCell>{row.status}</TableCell>
                          <TableCell align="right">{
                            row.total != null || row.amount_paid != null
                              ? Number(row.total ?? row.amount_paid).toFixed(2)
                              : '—'
                          }</TableCell>
                          <TableCell align="right">
                            <Tooltip title="Download invoice">
                              <span>
                                <IconButton onClick={() => handleDownload(row)} disabled={!!downloading[row.stripe_invoice_id]} size="small">
                                  {downloading[row.stripe_invoice_id] ? <CircularProgress size={18} /> : <DownloadIcon fontSize="small" />}
                                </IconButton>
                              </span>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={6} align="center">
                          <Typography variant="body2" color="text.secondary">No invoices to display yet.</Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
        </>
      )}
    </Box>
  );
}


